import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import back_n from '../../Common/Images/back_n.gif';
import back_o from '../../Common/Images/back_o.gif';
import continue_n from '../../Common/Images/continue_n.gif';
import continue_o from '../../Common/Images/continue_o.gif';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface ContentItem {
    id: string;
    name: string;
}

interface UnitItem extends ContentItem {
    isSelected: boolean;
}

interface ActivityItem {
    id: string;
    fileId?: string;
    name: string;
    type: 'chapter' | 'lesson';
    levelNo: number;
    unitCode: string;
    parentCode: string;
    isSelected: boolean;
    status?: 'Green' | 'Red';
}

const EL_SetWorkPhase2: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const state = location.state || {};
    const [isLoading, setIsLoading] = useState(false);
    const { editingContext, isEditMode, workId, returnPath, parentReturnPath } = state;

    const lessonPosition = state.lessonPosition || 0;
    const studentName = state.studentName || searchParams.get('StudentName') || state.login;
    const teacherName =
        state.teacherName ||
        searchParams.get('TeacherName') ||
        state.teacherLogin ||
        searchParams.get('TeacherLogin') ||
        '';
    const workTitle = state.workTitle || searchParams.get('WorkTitle') || '';
    const startDate = state.startDate || searchParams.get('StartDate') || '';
    const finishDate = state.finishDate || searchParams.get('FinishDate') || '';
    const startTime = state.startTime || searchParams.get('StartTime') || '00:00';
    const endTime = state.endTime || searchParams.get('EndTime') || '23:59';

    const familyCode = state.familyCode || searchParams.get('Work2FamilyCode');
    const login = state.login || searchParams.get('Work2Login');
    const gradeYear = state.gradeYear || searchParams.get('GradeYear');
    const schoolCode = state.schoolCode || searchParams.get('SchoolCode');
    const [selectedSubject, setSelectedSubject] = useState<string>(
        editingContext?.subject || 'Mathematics'
    );

    const [levels, setLevels] = useState<ContentItem[]>([]);
    const [selectedLevel, setSelectedLevel] = useState<string>('');

    const [units, setUnits] = useState<UnitItem[]>([]);
    const [selectedUnit, setSelectedUnit] = useState<UnitItem | null>(null);

    const [activities, setActivities] = useState<ActivityItem[]>([]);

    const [selectAll, setSelectAll] = useState(false);
    const [selectAllUnits, setSelectAllUnits] = useState(false);
    const [selectGreen, setSelectGreen] = useState(false);

    const [isBackHovered, setIsBackHovered] = useState(false);
    const [isContinueHovered, setIsContinueHovered] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchLevels = async () => {
            if (!selectedSubject || !familyCode) return;
            setIsLoading(true);
            try {
                const params = new URLSearchParams({
                    SchoolCode: schoolCode || '',
                    FamilyCode: familyCode || '',
                    Subject: selectedSubject,
                    GradeYear: String(gradeYear || ''),
                    ClassCode:
                        state.classCode ||
                        searchParams.get('ClassCode') ||
                        '',
                    TeacherLogin:
                        state.teacherLogin ||
                        searchParams.get('TeacherLogin') ||
                        searchParams.get('Teacher') ||
                        ''
                });

                const response = await fetch(`${API_BASE_URL}api/get_phase2_levels/?${params.toString()}`);
                const data = await response.json();
                const levelData = Array.isArray(data) ? data : [];
                setLevels(levelData);

                if (editingContext?.dicCode && editingContext?.subject === selectedSubject) {
                    const found = levelData.find((l: any) => String(l.id) === String(editingContext.dicCode));
                    if (found) {
                        setSelectedLevel(found.id);
                    }
                }

            } catch (error) {
                console.error("Failed to fetch levels", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLevels();
    }, [selectedSubject, familyCode, schoolCode]);

    useEffect(() => {
        const fetchLevelContent = async () => {
            if (!selectedLevel) return;
            setIsLoading(true);
            try {
                const params = new URLSearchParams({
                    Level: selectedLevel,
                    SchoolCode: schoolCode || '',
                    FamilyCode: familyCode || '',
                    Login: login || ''
                });

                const response = await fetch(`${API_BASE_URL}api/get_phase2_level_content/?${params.toString()}`);
                const data = await response.json();

                const isEditingThisLevel = editingContext && (String(editingContext.dicCode) === String(selectedLevel));

                const existingIds = new Set<string>();
                const selectedUnitIds = new Set<string>();

                if (isEditingThisLevel && editingContext.selectedActivities) {
                    const raw = editingContext.selectedActivities;

                    if (Array.isArray(raw)) {
                        raw.forEach((id: any) => existingIds.add(String(id).trim()));
                    } else if (typeof raw === 'string') {
                        raw.split(',').forEach((id: string) => existingIds.add(id.trim()));
                    }
                }

                if (data && Array.isArray(data.activities)) {
                    const items = data.activities.map((a: any) => {
                        const isSelected = existingIds.has(String(a.id)) || (a.fileId && existingIds.has(String(a.fileId)));
                        if (isSelected && a.unitCode) {
                            selectedUnitIds.add(a.unitCode);
                        }
                        return {
                            ...a,
                            isSelected
                        };
                    });
                    setActivities(items);
                }

                if (data && Array.isArray(data.units)) {
                    setUnits(data.units.map((u: any) => ({ ...u, isSelected: selectedUnitIds.has(String(u.id)) })));
                }
            } catch (error) {
                console.error("Failed to fetch level content", error);
            } finally {
                setIsLoading(false);
            }
        };

        setUnits([]);
        setActivities([]);
        setSelectGreen(false);
        setSelectAll(false);
        setSelectAllUnits(false);
        fetchLevelContent();
    }, [selectedLevel, schoolCode, familyCode, login]);

    const handleUnitClick = (unit: UnitItem) => {
        setSelectedUnit(unit);
    };

    const handleUnitCheckboxChange = (unitId: string, checked: boolean) => {
        const newUnits = units.map(u =>
            u.id === unitId ? { ...u, isSelected: checked } : u
        );
        setUnits(newUnits);

        setActivities(prev => prev.map(a => {
            if (a.unitCode === unitId) {
                return { ...a, isSelected: checked };
            }
            return a;
        }));

        const areAllUnitsSelected = newUnits.length > 0 && newUnits.every(u => u.isSelected);
        setSelectAllUnits(areAllUnitsSelected);
        setSelectAll(areAllUnitsSelected);
    };

    const handleChapterCheckboxChange = (chapterId: string, checked: boolean) => {
        setActivities(prev => prev.map(a => {
            if (a.id === chapterId) return { ...a, isSelected: checked };
            if (a.parentCode === chapterId) return { ...a, isSelected: checked };
            return a;
        }));
    };

    const handleSubjectChange = (subject: string) => {
        setSelectedSubject(subject);
        setSelectedLevel('');
        setSelectedUnit(null);
        setUnits([]);
        setActivities([]);
        setSelectAll(false);
        setSelectAllUnits(false);
        setSelectGreen(false);
    };

    const handleLessonToggle = (id: string) => {
        setActivities(prev => prev.map(a =>
            a.id === id ? { ...a, isSelected: !a.isSelected } : a
        ));
    };

    const handleSelectAllUnitsToggle = (checked: boolean) => {
        setSelectAllUnits(checked);
        setSelectAll(checked);
        setUnits(prev => prev.map(u => ({ ...u, isSelected: checked })));
        setActivities(prev => prev.map(a => ({ ...a, isSelected: checked })));
    };

    const handleSelectAllToggle = (checked: boolean) => {
        setSelectAll(checked);
        setSelectAllUnits(checked);
        setUnits(prev => prev.map(u => ({ ...u, isSelected: checked })));
        setActivities(prev => prev.map(a => ({ ...a, isSelected: checked })));
    };

    const handleSelectGreenToggle = (checked: boolean) => {
        setSelectGreen(checked);
        setActivities(prev => prev.map(a => {
            if (a.status === 'Green') return { ...a, isSelected: checked };
            return a;
        }));
    };

    const handleContinue = async () => {
        const selectedItems = activities.filter(
            a => a.isSelected && a.type === 'lesson'
        );

        if (selectedItems.length === 0) {
            setAlertInfo({
                show: true,
                message: "Please select at least one lesson."
            });
            return;
        }

        try {
            setIsLoading(true);

            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('Subject', selectedSubject);
            nextParams.set('Level', selectedLevel);

            const stateData = {
                studentName,
                familyCode,
                login,
                gradeYear,
                schoolCode,
                subject: selectedSubject,
                level: selectedLevel,
                workTitle,
                startDate,
                finishDate,
                startTime,
                endTime,

                selectedLessons: selectedItems.map(item => ({
                    id: item.fileId || item.id,
                    name: item.name
                })),

                isEditMode: isEditMode || !!searchParams.get('WorkID'),
                workId: workId || searchParams.get('WorkID'),
                returnPath,
                parentReturnPath,
                lessonPosition
            };

            navigate(
                {
                    pathname: '/SCHOOL/Teacher/EL_SetWorkPhase3',
                    search: `?${nextParams.toString()}`
                },
                { state: stateData }
            );

        } catch (error) {
            console.error('Prepare add lessons failed:', error);

            setAlertInfo({
                show: true,
                message: 'Unable to prepare lessons.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    
    const styles = {
        container: {
            padding: '10px', fontFamily: 'Arial, sans-serif', backgroundColor: '#fff', fontSize: '12px',
            height: isMobile ? 'auto' : '97.8dvh', minHeight: '100%', display: 'flex', flexDirection: 'column' as 'column', overflow: isMobile ? 'visible' : 'hidden'
        },
        headerText: { color: '#5B91B5', fontSize: '10pt', fontWeight: 'bold', marginBottom: '10px', flexShrink: 0 },
        radioGroup: { display: 'flex', gap: '20px', marginBottom: '10px', alignItems: 'center', flexShrink: 0 },
        radioLabel: { cursor: 'pointer', fontWeight: 'bold', color: '#003399' },

        columnsContainer: {
            display: 'flex', gap: '10px', flex: 1, flexDirection: isMobile ? 'column' as 'column' : 'row' as 'row',
            overflow: isMobile ? 'visible' : 'hidden', marginBottom: '10px'
        },
        column: {
            flex: 1, border: '1px solid #7F9DB9', display: 'flex', flexDirection: 'column' as 'column',
            height: isMobile ? '300px' : '100%', marginBottom: isMobile ? '10px' : '0'
        },
        columnHeader: {
            backgroundColor: '#548BED', color: 'white', fontWeight: 'bold', padding: '5px', textAlign: 'center' as 'center',
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
        },
        listContainer: { flex: 1, overflowY: 'auto' as 'auto', padding: '5px', backgroundColor: '#fff' },

        listItem: { padding: '4px', cursor: 'pointer', borderBottom: '1px solid #eee', fontWeight: 'bold' as 'bold' },
        selectedItem: { padding: '4px', cursor: 'pointer', borderBottom: '1px solid #eee', backgroundColor: '#65a5f8', fontWeight: 'bold', color: 'white' },
        selectedUnitItem: { padding: '4px', cursor: 'pointer', borderBottom: '1px solid #eee', color: 'black', backgroundColor: '#fff', fontWeight: 'bold' as 'bold' },

        chapterRow: {
            fontWeight: 'bold' as 'bold',
            borderBottom: '1px solid white',
            padding: '4px',
            backgroundColor: 'white',
            color: 'black'
        },
        lessonRowGreen: {
            backgroundColor: '#0be208', borderBottom: '1px solid white', padding: '4px', paddingLeft: '20px'
        },
        lessonRowRed: {
            backgroundColor: 'red', textDecoration: 'line-through', borderBottom: '1px solid white', padding: '4px', paddingLeft: '20px'
        },

        footerTable: { width: '100%', marginTop: 'auto', border: '0px', flexShrink: 0 },
        footerBar: { backgroundColor: '#548BED', padding: '5px' },
        controls: { padding: '5px', backgroundColor: '#EBF1FD', borderBottom: '1px solid #ccc', flexShrink: 0, fontWeight: 'bold' as 'bold' },
    };

    return (
        <div style={styles.container}>
            {isLoading && <Loading />}
            <table id='tblHeader1' style={{ width: '100%', border: '0' }} cellPadding={0} cellSpacing={0}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4' }}>
                        <td className='lableStyle' style={{ height: '26px', paddingLeft: '5px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', alignItems: 'left' }}>
                            eDealer: {familyCode} | Tutor: {teacherName}
                        </td>
                        <td className='lableStyle' style={{ height: '26px', paddingRight: '15px', verticalAlign: 'middle', fontFamily: 'Arial', fontSize: '13px', color: '#548BED', textAlign: 'right' }}>
                            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;
                            <a href="#" id='lnkLogout' onClick={(e) => {
                                e.preventDefault();
                                const finalReturnPath = returnPath || searchParams.get('ReturnPath');
                                if (finalReturnPath) {
                                    navigate(finalReturnPath, { replace: true });
                                } else {
                                    navigate({
                                        pathname: '/SCHOOL/Teacher/EL_ViewWork',
                                        search: `?${searchParams.toString()}`
                                    });
                                }
                            }}>Cancel</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table id='tblHeader2' style={{ width: '100%', border: '0', borderCollapse: 'collapse', marginTop: '3px', marginBottom: '15px' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#E4E4E4', fontFamily: 'Arial', fontSize: '16px' }}>
                        <td className='PageHeader' style={{ height: '26px', verticalAlign: 'middle' }}></td>
                        <td style={{ height: '26px', verticalAlign: 'middle', textAlign: 'right' }}></td>
                    </tr>
                </tbody>
            </table>
            <div style={styles.headerText}>
                Lesson selection to set work for class: {gradeYear}
            </div>

            <div style={styles.radioGroup}>
                <span style={{ fontWeight: 'bold', color: '#000' }}>Select a subject:</span>
                {['Mathematics', 'English', 'Science'].map(sub => (
                    <label key={sub} style={styles.radioLabel}>
                        <input type="radio" style={{ marginTop: '0px' }} checked={selectedSubject === sub} onChange={() => handleSubjectChange(sub)} /> {sub}
                    </label>
                ))}
            </div>

            <div style={{ fontWeight: 'bold', color: '#000', marginBottom: '10px' }}>Select the work you want to assign and press the continue button:</div>

            <div style={styles.columnsContainer}>
                <div style={styles.column}>
                    <div style={styles.controls}>Level</div>
                    <div style={styles.listContainer}>
                        {levels.map(lvl => (
                            <div key={lvl.id} style={selectedLevel === lvl.id ? styles.selectedItem : styles.listItem}
                                onClick={() => setSelectedLevel(lvl.id)}>
                                {lvl.name}
                            </div>
                        ))}
                    </div>
                </div>

                <div style={styles.column}>
                    <div style={styles.controls}>
                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <input type="checkbox" checked={selectAllUnits} onChange={(e) => handleSelectAllUnitsToggle(e.target.checked)} style={{ marginRight: '8px' }} />
                            Unit
                        </label>
                    </div>
                    <div style={styles.listContainer}>
                        {units.map(unit => (
                            <div
                                key={unit.id}
                                style={selectedUnit?.id === unit.id ? styles.selectedUnitItem : styles.listItem}
                                onClick={() => handleUnitClick(unit)}>
                                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <input type="checkbox" checked={unit.isSelected} onChange={(e) => handleUnitCheckboxChange(unit.id, e.target.checked)}
                                        onClick={(e) => e.stopPropagation()} style={{ marginRight: '8px' }} />
                                    {unit.name}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={styles.column}>
                    <div style={styles.controls}>
                        <label style={{ marginRight: '15px' }}>
                            <input type="checkbox" checked={selectAll} onChange={(e) => handleSelectAllToggle(e.target.checked)} /> Activities
                        </label>
                        <label style={{ color: '#bbbbbb', marginRight: '15px' }}>(G) Game  (L) Lesson  (T) Test</label>
                        <label>
                            <input type="checkbox" checked={selectGreen} onChange={(e) => handleSelectGreenToggle(e.target.checked)} /> Select Green
                        </label>
                    </div>

                    <div style={styles.listContainer}>
                        {activities.map(item => {
                            if (item.type === 'chapter') {
                                return (
                                    <div key={item.id} style={styles.chapterRow}>
                                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <input
                                                type="checkbox"
                                                checked={item.isSelected}
                                                onChange={(e) => handleChapterCheckboxChange(item.id, e.target.checked)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <u>{item.name}</u>
                                        </label>
                                    </div>
                                );
                            } else {
                                return (
                                    <div key={item.id} style={item.status === 'Green' ? styles.lessonRowGreen : styles.lessonRowRed}>
                                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <input
                                                type="checkbox"
                                                checked={item.isSelected}
                                                onChange={() => handleLessonToggle(item.id)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            {item.name}
                                        </label>
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>

            </div>

            <table style={styles.footerTable}>
                <tbody>
                    <tr><td style={styles.footerBar}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 20px' }}>
                            <img
                                src={isBackHovered ? back_o : back_n}
                                alt="Back"
                                onClick={() => {
                                    const finalReturnPath = returnPath || searchParams.get('ReturnPath');
                                    if (lessonPosition > 0 && finalReturnPath) {
                                        navigate(finalReturnPath, { replace: true });
                                    } else {
                                        navigate({
                                            pathname: '/SCHOOL/Teacher/EL_SetWork',
                                            search: `?${searchParams.toString()}`
                                        }, {
                                            state: { ...state, returnPath: finalReturnPath }
                                        });
                                    }
                                }}
                                onMouseEnter={() => setIsBackHovered(true)}
                                onMouseLeave={() => setIsBackHovered(false)}
                                style={{ cursor: 'pointer' }}
                            />
                            <img src={isContinueHovered ? continue_o : continue_n} alt="Continue" onClick={handleContinue}
                                onMouseEnter={() => setIsContinueHovered(true)} onMouseLeave={() => setIsContinueHovered(false)} style={{ cursor: 'pointer' }} />
                        </div>
                    </td></tr>
                </tbody>
            </table>
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
};

export default EL_SetWorkPhase2;